import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import DashboardLayout from '../../components/layout/DashboardLayout';
import MapPicker from '../../components/common/MapPicker';
import MapView from '../../components/common/MapView';
import EpsgSelect from '../../components/common/EpsgSelect';
import { PROJECT_QUERY } from '../../graphql/queries';
import {
  UPDATE_PROJECT_MUTATION,
  REQUEST_PROJECT_TRANSFER_MUTATION,
  CANCEL_PROJECT_TRANSFER_MUTATION,
} from '../../graphql/mutations';
import { useAuth } from '../../contexts/AuthContext';
import { truncateName } from '../../utils/text';
import styles from './ProjectSettings.module.css';
import { seesAllProjects } from '../../utils/staff';
import { managesProject, projectRoleFor } from '../../utils/projectRole';
import ProjectMembersPanel from './ProjectMembersPanel';
import Icon from '../../components/common/Icon';

const ProjectSettings = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data, loading } = useQuery(PROJECT_QUERY, {
    variables: { id: projectId },
    skip: !projectId,
  });

  const [form, setForm] = useState({
    name: '',
    description: '',
    location: '',
    latitude: null as number | null,
    longitude: null as number | null,
    epsg: null as number | null,
    overviewPanel: 'map',
    status: 'active',
  });

  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const p = data?.project;
    if (!p) return;
    setForm({
      name:        p.name        ?? '',
      description: p.description ?? '',
      location:    p.location    ?? '',
      latitude:    p.latitude    ?? null,
      longitude:   p.longitude   ?? null,
      epsg:        p.epsg        ?? null,
      overviewPanel: p.overviewPanel ?? 'map',
      status:      p.status      ?? 'active',
    });
  }, [data]);

  const [transferEmail, setTransferEmail] = useState('');
  const [transferSent, setTransferSent] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);

  const [updateProject, { loading: saving }] = useMutation(UPDATE_PROJECT_MUTATION, {
    onCompleted: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (err) => setSaveError(err.message),
    refetchQueries: [{ query: PROJECT_QUERY, variables: { id: projectId } }],
  });

  const [requestTransfer, { loading: transferring }] = useMutation(REQUEST_PROJECT_TRANSFER_MUTATION, {
    onCompleted: () => {
      setTransferEmail('');
      setTransferError(null);
      setTransferSent(true);
      setTimeout(() => setTransferSent(false), 4000);
    },
    onError: (err) => setTransferError(err.message),
    refetchQueries: [{ query: PROJECT_QUERY, variables: { id: projectId } }],
  });

  const [cancelTransfer, { loading: cancelling }] = useMutation(CANCEL_PROJECT_TRANSFER_MUTATION, {
    onError: (err) => setTransferError(err.message),
    refetchQueries: [{ query: PROJECT_QUERY, variables: { id: projectId } }],
  });

  const handleSave = () => {
    if (!form.name.trim()) return;
    setSaveError(null);
    if (transferEmail.trim()) {
      setTransferError(null);
      requestTransfer({ variables: { projectId, email: transferEmail.trim() } });
    }
    updateProject({
      variables: {
        id: projectId,
        input: {
          name:        form.name.trim(),
          description: form.description.trim() || null,
          location:    form.location.trim()    || null,
          latitude:    form.latitude,
          longitude:   form.longitude,
          epsg:        form.epsg,
          overviewPanel: form.overviewPanel,
          status:      form.status,
        },
      },
    });
  };

  const project = data?.project;
  const isOwner = !!project && user?.id === project.owner?.id;
  const canEdit = isOwner || seesAllProjects(user);

  return (
    <DashboardLayout sidebarContext="project" projectId={projectId}>
      {loading ? (
        <div className="flex items-center justify-center py-16 text-on-surface-variant">
          <Icon name="refresh" className="animate-spin mr-2" />
          Loading…
        </div>
      ) : !project ? (
        <div className="text-center py-16">
          <p className="text-on-surface-variant">Project not found</p>
          <button onClick={() => navigate('/projects')} className="mt-4 text-primary hover:underline text-sm">
            Back to projects
          </button>
        </div>
      ) : (
        <div className="space-y-8 max-w-3xl">
          {/* Breadcrumb */}
          <div>
            <div className="flex items-center gap-2 text-xs text-on-surface-variant mb-2">
              <button onClick={() => navigate('/projects')} className="hover:text-primary transition-colors">Projects</button>
              <Icon name="chevron_right" className="text-sm" />
              <button onClick={() => navigate(`/projects/${projectId}`)} className="hover:text-primary transition-colors" title={project.name}>{truncateName(project.name)}</button>
              <Icon name="chevron_right" className="text-sm" />
              <span>Settings</span>
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Project Settings</h2>
            {!canEdit && (
              <p className={styles.readOnlyNote}>Only the project owner can change these settings.</p>
            )}
          </div>

          {/* Form */}
          <div className={styles.card}>
            <h3 className={styles.sectionTitle}>General</h3>

            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="project-name">Project Name *</label>
              <input
                id="project-name"
                className="input-field"
                disabled={!canEdit}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Hudson Yards Phase 2"
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="project-description">Description</label>
              <textarea
                id="project-description"
                className="input-field resize-none"
                disabled={!canEdit}
                rows={3}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Brief description of the project…"
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="project-status">Status</label>
              <select
                id="project-status"
                className="input-field"
                disabled={!canEdit}
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="overview-panel">Overview panel</label>
              <select
                id="overview-panel"
                className="input-field"
                disabled={!canEdit}
                value={form.overviewPanel}
                onChange={(e) => setForm((f) => ({ ...f, overviewPanel: e.target.value }))}
              >
                <option value="map">Map</option>
                <option value="walkthroughs">Walkthrough cards</option>
              </select>
              <p className={styles.hint}>What the project overview shows beside the stats: the location map, or tiles for the Matterport tour and 360 videos.</p>
            </div>
          </div>

          {/* Location */}
          <div className={styles.card}>
            <h3 className={styles.sectionTitle}>Location</h3>

            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="project-location">Location Name</label>
              <input
                id="project-location"
                className="input-field"
                disabled={!canEdit}
                value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                placeholder="e.g. New York City, NY"
              />
            </div>

            <div className={styles.fieldGroup}>
              <span className={styles.label}>Map Pin</span>
              {canEdit ? (
                <MapPicker
                  lat={form.latitude}
                  lng={form.longitude}
                  onChange={(lat, lng) => setForm((f) => ({ ...f, latitude: lat, longitude: lng }))}
                />
              ) : form.latitude != null && form.longitude != null ? (
                <MapView lat={form.latitude} lng={form.longitude} label={project.name} />
              ) : (
                <p className={styles.hint}>No map pin set.</p>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="project-epsg">Coordinate System (EPSG)</label>
              <EpsgSelect id="project-epsg" value={form.epsg} disabled={!canEdit} onChange={(code) => setForm((f) => ({ ...f, epsg: code }))} />
              <p className={styles.hint}>Optional — the coordinate reference system the point clouds use (e.g. survey data in British National Grid). Leave unset for local/indoor scans.</p>
            </div>

            {form.latitude != null && form.longitude != null && (
              <div className={styles.coordRow}>
                <span>Latitude: <strong>{form.latitude.toFixed(6)}</strong></span>
                <span>Longitude: <strong>{form.longitude.toFixed(6)}</strong></span>
                {canEdit && (
                  <button
                    className={styles.clearBtn}
                    onClick={() => setForm((f) => ({ ...f, latitude: null, longitude: null }))}
                  >
                    Clear pin
                  </button>
                )}
              </div>
            )}
          </div>

          <ProjectMembersPanel
            projectId={projectId!}
            ownerId={project.owner?.id ?? ''}
            canManage={managesProject(projectRoleFor(project, user))}
          />

          {/* Transfer ownership — owner only */}
          {isOwner && (
            <div className={styles.transferCard}>
              <h3 className={styles.transferTitle}>Transfer project ownership</h3>
              <p className={styles.hint}>
                Transfer this project to another account. When you save, the person you enter
                receives an email with the project details and an accept button — the project
                (and all of its files and storage) moves to their account only after they accept.
                If their account doesn't have enough storage for this project, the transfer is rejected.
              </p>

              {project.pendingTransfer?.status === 'pending' && (
                <div className={styles.transferPending}>
                  <span>
                    Pending transfer to <strong>{project.pendingTransfer.toEmail}</strong>
                    {' — '}sent {new Date(project.pendingTransfer.createdAt).toLocaleDateString()}
                  </span>
                  <button
                    className={styles.clearBtn}
                    disabled={cancelling}
                    onClick={() => cancelTransfer({ variables: { projectId } })}
                  >
                    {cancelling ? 'Cancelling…' : 'Cancel transfer'}
                  </button>
                </div>
              )}

              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="transfer-email">Recipient email</label>
                <input
                  id="transfer-email"
                  className="input-field"
                  type="email"
                  value={transferEmail}
                  onChange={(e) => { setTransferEmail(e.target.value); setTransferError(null); }}
                  placeholder="name@company.com"
                />
                {transferError && <p className={styles.transferError}>{transferError}</p>}
                {transferSent && <p className={styles.transferSent}>✓ Transfer email sent</p>}
                {transferring && <p className={styles.hint}>Sending transfer email…</p>}
              </div>
            </div>
          )}

          {/* Actions */}
          {saveError && <p className={styles.saveError} role="alert">{saveError}</p>}
          <div className={styles.actions}>
            <button
              onClick={() => navigate(`/projects/${projectId}`)}
              className="px-5 py-2.5 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors"
            >
              {canEdit ? 'Cancel' : 'Back to project'}
            </button>
            {canEdit && (
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="btn-primary px-6 py-2.5 rounded-lg text-sm bg-gradient-to-r from-primary to-primary-container disabled:opacity-50"
              >
                {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ProjectSettings;
